// ProgressProvider.tsx
import React, {createContext, useContext, useRef, useState} from 'react';
import { FullPageLoader } from './full-page-loader';

type Ctx = { start: () => void; stop: () => void };
const ProgressCtx = createContext<Ctx | null>(null);

export function useGlobalProgress() {
  const ctx = useContext(ProgressCtx);
  if (!ctx) throw new Error('ProgressProvider missing');
  return ctx;
}

export function ProgressProvider({children}: {children: React.ReactNode}) {
  const [visible, setVisible] = useState(false);
  const countRef = useRef(0);
  const minTimer = useRef<number | null>(null);
  const showTimer = useRef<number | null>(null);

  const start = () => {
    countRef.current++;
    // petit délai pour éviter le flicker sur des calls ultra courts
    if (!showTimer.current) {
      showTimer.current = window.setTimeout(() => {
        setVisible(true);
        showTimer.current = null;
        // garde visible au moins 300ms
        minTimer.current = window.setTimeout(() => { minTimer.current = null; }, 300);
      }, 120);
    }
  };

  const stop = () => {
    countRef.current = Math.max(0, countRef.current - 1);
    if (countRef.current === 0) {
      const hide = () => setVisible(false);
      // attend la fin de la durée minimale si active
      if (minTimer.current) {
        const t = window.setTimeout(() => hide(), 0);
        return () => window.clearTimeout(t);
      }
      hide();
    }
  };

  return (
    <ProgressCtx.Provider value={{start, stop}}>
      {children}
      {visible && (
        <FullPageLoader className='fixed top-0 left-0 z-[9999] bg-alt' />
      )}
    </ProgressCtx.Provider>
  );
}
